<section class="document-settings" aria-label="Document settings">
  @if (validationError) {
    <p class="setting-warning" role="alert">{{ validationError }}</p>
  }
  <fieldset [disabled]="disabled" (focusout)="blur()">
    <legend>Reusable brand profile</legend>
    <label
      >Exact profile version
      <select
        uiControl
        name="document-brand-profile"
        data-document-setting="profile"
        aria-label="Document brand profile"
        [ngModel]="brandKey"
        (ngModelChange)="chooseProfile($event)"
        [disabled]="
          disabled ||
          assetsLoading ||
          (!assetCatalog?.canSelect && !composition.branding.profile)
        "
      >
        <option value="">Local document branding</option>
        @if (composition.branding.profile && !selectedBrand) {
          <option [value]="brandKey">
            Unavailable retained profile · v{{
              composition.branding.profile.version
            }}
          </option>
        }
        @for (brand of assetCatalog?.brands ?? []; track profileKey(brand)) {
          <option [value]="profileKey(brand)">
            {{ brand.name }} · v{{ brand.reference.version
            }}{{ brand.isLatest ? "" : " · Retained" }}
          </option>
        }
      </select>
    </label>
    <button
      uiButton="secondary"
      type="button"
      (click)="loadAssets.emit()"
      [disabled]="disabled || assetsLoading"
    >
      {{ assetsLoading ? "Loading profiles…" : "Refresh brand profiles" }}
    </button>
    @if (assetsError) {
      <p role="alert" class="setting-warning">{{ assetsError }}</p>
    }
    @if (selectedBrand; as brand) {
      <p class="setting-guidance">
        {{ brand.scopeTypeCode
        }}{{ brand.scopeKey ? " · " + brand.scopeKey : "" }} · immutable v{{
          brand.reference.version
        }}
      </p>
      <p class="setting-guidance hash">SHA-256 {{ brand.reference.sha256 }}</p>
      <p class="setting-guidance">
        Profile governs font, logo, accent and watermark. Header, footer and box
        styling remain editable. Remove the profile to restore local document
        branding.
      </p>
      <p class="setting-guidance">
        Font {{ brand.style.fontCode }} · Logo
        {{
          brand.style.logo
            ? "v" +
              brand.style.logo.version +
              " · " +
              brand.style.logoWidthPt +
              " × " +
              brand.style.logoHeightPt +
              " pt · " +
              brand.style.logoAlignment
            : "None"
        }}
        · Watermark {{ brand.style.watermarkText || "None" }}
      </p>
    }
  </fieldset>
  <fieldset [disabled]="disabled" (focusout)="blur()">
    <legend>Page setup</legend>
    <label>
      Page size
      <select
        uiControl
        name="document-size"
        data-document-setting="size"
        aria-label="Document page size"
        [ngModel]="composition.page.size"
        (ngModelChange)="pageText('size', $event)"
      >
        @if (!knownSize) {
          <option [value]="composition.page.size" data-unavailable="size">
            Unavailable: {{ composition.page.size || "Unspecified" }}
          </option>
        }
        <option value="letter">Letter</option>
        <option value="a4">A4</option>
      </select>
    </label>
    <label>
      Margin (pt)
      <input
        uiControl
        type="number"
        name="document-margin"
        data-document-setting="marginPt"
        aria-label="Document margin in points"
        min="36"
        max="72"
        step="any"
        [ngModel]="composition.page.marginPt"
        (ngModelChange)="pageNumber('marginPt', $event)"
      />
    </label>
    <label>
      Font
      <select
        uiControl
        name="document-font"
        data-document-setting="fontCode"
        aria-label="Document font"
        [disabled]="disabled || !!composition.branding.profile || !fonts.length"
        [ngModel]="composition.page.fontCode"
        (ngModelChange)="pageText('fontCode', $event)"
      >
        @if (!knownFont) {
          <option [value]="composition.page.fontCode" data-unavailable="font">
            Unavailable: {{ composition.page.fontCode || "Unspecified" }}
          </option>
        }
        @for (font of fonts; track font.code) {
          <option [value]="font.code">{{ font.label }}</option>
        }
      </select>
    </label>
    <div class="paired-settings">
      <label>
        Body size (pt)
        <input
          uiControl
          type="number"
          name="document-body-size"
          data-document-setting="fontSizePt"
          aria-label="Document body size in points"
          aria-description="Updates blocks currently using the body size; preserves other block sizes."
          min="8"
          max="24"
          step="any"
          [ngModel]="composition.page.fontSizePt"
          (ngModelChange)="pageNumber('fontSizePt', $event)"
        />
      </label>
      <label>
        Line height
        <input
          uiControl
          type="number"
          name="document-line-height"
          data-document-setting="lineHeight"
          aria-label="Document line height"
          min="1"
          max="1.8"
          step="0.05"
          [ngModel]="composition.page.lineHeight"
          (ngModelChange)="pageNumber('lineHeight', $event)"
        />
      </label>
    </div>
  </fieldset>
  <fieldset [disabled]="disabled" (focusout)="blur()">
    <legend>Header and footer</legend>
    <label>
      Header wording
      <textarea
        uiControl
        name="document-header"
        data-document-setting="headerText"
        aria-label="Document header wording"
        rows="2"
        maxlength="100"
        [ngModel]="composition.branding.headerText"
        (ngModelChange)="branding('headerText', $event)"
      ></textarea>
    </label>
    <label>
      Footer wording
      <textarea
        uiControl
        name="document-footer"
        data-document-setting="footerText"
        aria-label="Document footer wording"
        rows="2"
        maxlength="160"
        [ngModel]="composition.branding.footerText"
        (ngModelChange)="branding('footerText', $event)"
      ></textarea>
    </label>
    <p class="setting-guidance">Page numbers are added in the final PDF.</p>
  </fieldset>
  <fieldset
    [disabled]="disabled || !!composition.branding.profile"
    (focusout)="blur()"
  >
    <legend>
      Local branding{{
        composition.branding.profile ? " · Profile selected" : ""
      }}
    </legend>
    <label>
      Accent color
      <span class="accent-controls">
        <input
          uiControl
          type="text"
          name="document-accent"
          data-document-setting="accentColor"
          aria-label="Document accent color"
          aria-description="Six-digit RGB color, for example #183B56."
          maxlength="7"
          pattern="#[0-9a-fA-F]{6}"
          spellcheck="false"
          [ngModel]="composition.branding.accentColor"
          (ngModelChange)="branding('accentColor', $event)"
        />
        @if (knownAccent) {
          <input
            uiControl
            type="color"
            name="document-accent-picker"
            aria-label="Choose document accent color"
            [ngModel]="composition.branding.accentColor"
            (ngModelChange)="branding('accentColor', $event)"
          />
        }
      </span>
    </label>
    <label>
      Logo
      <select
        uiControl
        name="document-logo"
        data-document-setting="logoCode"
        aria-label="Document logo"
        [ngModel]="composition.branding.logoCode"
        (ngModelChange)="branding('logoCode', $event)"
      >
        @if (!knownLogo) {
          <option
            [value]="composition.branding.logoCode"
            data-unavailable="logo"
          >
            Unavailable: {{ composition.branding.logoCode || "Unspecified" }}
          </option>
        }
        @for (logo of logos; track logo.code) {
          <option [value]="logo.code">{{ logo.label }}</option>
        }
      </select>
    </label>
    <label>
      Watermark
      <select
        uiControl
        name="document-watermark"
        data-document-setting="watermarkCode"
        aria-label="Document watermark"
        [ngModel]="composition.branding.watermarkCode"
        (ngModelChange)="branding('watermarkCode', $event)"
      >
        @if (!knownWatermark) {
          <option
            [value]="composition.branding.watermarkCode"
            data-unavailable="watermark"
          >
            Unavailable:
            {{ composition.branding.watermarkCode || "Unspecified" }}
          </option>
        }
        @for (watermark of watermarks; track watermark.code) {
          <option [value]="watermark.code">{{ watermark.label }}</option>
        }
      </select>
    </label>
    <p class="setting-guidance">
      The synthetic seal is fixed at 40 pt. The watermark uses fixed SYNTHETIC
      DRAFT wording and placement.
    </p>
    @if (
      !knownSize || !knownFont || !knownLogo || !knownWatermark || !knownAccent
    ) {
      <p class="setting-warning" role="status">
        Unavailable settings are retained. Choose a supported value before
        saving.
      </p>
    }
  </fieldset>
</section>
